import { jwtDecode } from 'jwt-decode';
import { post } from './api'; // Import the configured API client
import { API_CONFIG } from '../config/api.config';
import { UserProfile } from './userProfileService';

// Types
interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  token: string;
}

interface TokenPayload {
  sub: string; // email address
  companyId: string;
  roles: string[]; // Changed from 'role' to 'roles' array
  userId?: string;
  exp: number;
  iat: number;
}

interface UserInfo {
  username: string;
  displayName?: string;
  role: string; // For backward compatibility
  roles: string[]; // Add roles array
  companyId: string;
  userProfile?: UserProfile; // Add user profile data
}

// Token refresh settings
const TOKEN_REFRESH_THRESHOLD = 5 * 60; // Refresh token 5 minutes before expiration (in seconds)
let refreshTimerId: number | null = null;

// Helper function to extract display name from email
function extractDisplayName(email: string): string {
  // Extract the part before the @ symbol and capitalize first letters
  const nameParts = email.split('@')[0].split('.');
  return nameParts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// Helper function to process and store token
const processToken = (token: string): UserInfo | null => {
  try {
    const decodedToken = jwtDecode<TokenPayload>(token);

    if (!decodedToken || !decodedToken.sub) {
      return null;
    }

    localStorage.setItem('token', token);
    setupTokenRefresh(decodedToken.exp);

    // Set the primary role (for backward compatibility)
    const primaryRole =
      Array.isArray(decodedToken.roles) && decodedToken.roles.length > 0
        ? decodedToken.roles[0]
        : 'USER';

    // Return user info
    const userInfo: UserInfo = {
      username: decodedToken.sub,
      displayName: extractDisplayName(decodedToken.sub),
      role: primaryRole, // Set primary role for backward compatibility
      roles: Array.isArray(decodedToken.roles)
        ? decodedToken.roles
        : [primaryRole], // Include all roles
      companyId: decodedToken.companyId
    };

    localStorage.setItem('user', JSON.stringify(userInfo));
    return userInfo;
  } catch (decodeError) {
    return null;
  }
};

// Setup token refresh timer
const setupTokenRefresh = (expiryTime: number) => {
  // Clear any existing timer
  if (refreshTimerId !== null) {
    window.clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilRefresh =
    (expiryTime - currentTime - TOKEN_REFRESH_THRESHOLD) * 1000;

  // Only set timer if token isn't already expired or too close to expiration
  if (timeUntilRefresh > 0) {
    refreshTimerId = window.setTimeout(() => refreshToken(), timeUntilRefresh);
  }
};

// Silent token refresh
const refreshToken = async (): Promise<boolean> => {
  const token = localStorage.getItem('token');
  if (!token) {
    return false;
  }

  try {
    // Use the silent refresh endpoint
    const response = await post<LoginResponse>(
      API_CONFIG.endpoints.refreshToken || '/auth/refresh',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (response && response.token) {
      processToken(response.token);
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

// Check token on application start and setup refresh timer if needed
const initializeAuth = () => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const decodedToken = jwtDecode<TokenPayload>(token);
      const currentTime = Math.floor(Date.now() / 1000);

      // If token is valid, setup refresh timer
      if (decodedToken.exp > currentTime) {
        setupTokenRefresh(decodedToken.exp);
      } else {
        // If token is expired, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } catch (error) {
      // Clear potentially corrupted token
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  }
};

// Initialize authentication on service import
initializeAuth();

// Export the auth service as a named export
export const authService = {
  // Login user and get token
  login: async (email: string, password: string): Promise<UserInfo | null> => {
    const data: LoginRequest = { email, password };

    // Call the login endpoint
    const response = await post<LoginResponse>(
      API_CONFIG.endpoints.login,
      data as unknown as Record<string, unknown>
    );

    if (response && response.token) {
      return processToken(response.token);
    }

    return null;
  },

  // Get current user info
  getCurrentUser: (): UserInfo | null => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        return null;
      }

      const userInfo = JSON.parse(userData) as UserInfo;

      // Verify if we have a valid token for this user
      const token = localStorage.getItem('token');
      if (!token) {
        localStorage.removeItem('user');
        return null;
      }

      // Additional validation - check token expiration
      try {
        const decodedToken = jwtDecode<TokenPayload>(token);
        const currentTime = Math.floor(Date.now() / 1000);

        if (decodedToken.exp <= currentTime) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          return null;
        }

        // Verify token subject matches user
        if (decodedToken.sub !== userInfo.username) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          return null;
        }
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return null;
      }

      return userInfo;
    } catch (parseError) {
      localStorage.removeItem('user'); // Clear corrupted data
      return null;
    }
  },

  // Logout user
  logout: (): void => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear any refresh timer
    if (refreshTimerId !== null) {
      window.clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }

    try {
      const decodedToken = jwtDecode<TokenPayload>(token);
      const currentTime = Date.now() / 1000;

      // Check if token is expired
      const isValid = decodedToken.exp > currentTime;

      if (!isValid) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }

      return isValid;
    } catch (error) {
      return false;
    }
  },

  // Refresh token
  refreshToken
};
